import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Save, ArrowLeft, Mic, Paperclip, X, Download, Square, Play, Pause, Volume2, Trash2, Users, UserPlus } from 'lucide-react';
import axios from 'axios';
import AudioPlayer from './AudioPlayer';
import AudioErrorBoundary from './AudioErrorBoundary';
import ThemedDialog from './ThemedDialog';
import { useRecordingContext } from '../contexts/RecordingContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper: get image URL - uses pre-signed URLs from entry.images[] if available
const getImgUrl = (entry, imageId, variant = 'medium') => {
  // Find the image in the entry's images array to get pre-signed URL
  if (entry?.images) {
    const img = entry.images.find(i => i.id === imageId);
    if (img) {
      if (variant === 'thumb' && img.thumbUrl) return img.thumbUrl;
      if (variant === 'medium' && img.mediumUrl) return img.mediumUrl;
      if (variant === 'full' && img.fullUrl) return img.fullUrl;
    }
  }
  // Fallback to proxy endpoint
  return `${API}/journal/images/${imageId}?variant=${variant}`;
};

// Helper: get download URL from entry (JPEG conversion endpoint)
const getDownloadUrl = (entry, imageId) => {
  if (entry?.images) {
    const img = entry.images.find(i => i.id === imageId);
    if (img?.downloadUrl) {
      // If relative path, prepend backend URL
      if (img.downloadUrl.startsWith('http')) return img.downloadUrl;
      return `${BACKEND_URL}${img.downloadUrl}`;
    }
  }
  return `${API}/journal/images/${imageId}/download`;
};

const pageVariants = {
  initial: { opacity: 0, x: 30 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -30 }
};

const pageTransition = { duration: 0.25, ease: 'easeOut' };

function EditEntryPage() {
  const { entryId } = useParams();
  const navigate = useNavigate();
  const [entry, setEntry] = useState(null);
  const [content, setContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);

  // Image state
  const [existingImages, setExistingImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const imageInputRef = useRef(null);
  const [lightboxImage, setLightboxImage] = useState(null); // { imageUrl: string, downloadUrl: string|null }

  // Themed dialog state
  const [dialogConfig, setDialogConfig] = useState({ open: false, title: '', message: '', type: 'alert', onConfirm: null, onCancel: null });
  
  // Share state
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [shareLoading, setShareLoading] = useState(false);
  const [shareError, setShareError] = useState('');
  const [shareSuccess, setShareSuccess] = useState('');

  // Global recording context - disables swipe navigation while recording
  const { setIsRecording: setGlobalRecording } = useRecordingContext();

  // Audio recording state
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isAudioTranscribing, setIsAudioTranscribing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => { fetchEntry(); }, [entryId]);

  useEffect(() => {
    return () => {
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      newImages.forEach(img => URL.revokeObjectURL(img.preview));
    };
  }, [audioUrl]);

  const fetchEntry = async () => {
    try {
      const ts = Date.now();
      const response = await axios.get(`${API}/journal?_t=${ts}`);
      const foundEntry = response.data.find(e => e.id === entryId);
      if (foundEntry) {
        setEntry(foundEntry);
        setContent(foundEntry.content);
        // Prefer structured images, fall back to image_filenames
        if (foundEntry.images && foundEntry.images.length > 0) {
          setExistingImages(foundEntry.images.map(img => img.id));
        } else {
          setExistingImages(foundEntry.image_filenames || []);
        }
      }
    } catch (error) {
      console.error('Error fetching entry:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleContentChange = (e) => {
    setContent(e.target.value);
    setHasChanges(true);
  };

  // Image handlers
  const handleImageSelect = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    const totalImages = existingImages.length + newImages.length;
    const remaining = 10 - totalImages;
    const toAdd = files.slice(0, remaining);
    const imgs = toAdd.map(file => ({ file, preview: URL.createObjectURL(file) }));
    setNewImages(prev => [...prev, ...imgs]);
    setHasChanges(true);
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  const removeExistingImage = (index) => {
    setExistingImages(prev => prev.filter((_, i) => i !== index));
    setHasChanges(true);
  };

  const removeNewImage = (index) => {
    setNewImages(prev => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
    setHasChanges(true);
  };

  const uploadNewImages = async () => {
    const filenames = [];
    const imageRecords = [];
    for (const img of newImages) {
      const formData = new FormData();
      formData.append('file', img.file);
      const res = await axios.post(`${API}/journal/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      filenames.push(res.data.filename);
      if (res.data.image) imageRecords.push(res.data.image);
    }
    return { filenames, imageRecords };
  };

  // Audio handlers
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      audioChunksRef.current = [];
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };
      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        if (streamRef.current) streamRef.current.getTracks().forEach(track => track.stop());
      };
      mediaRecorderRef.current.start(100);
      setIsRecording(true);
      setGlobalRecording(true); // Disable swipe navigation
      setIsPaused(false);
      setRecordingTime(0);
      recordingTimerRef.current = setInterval(() => setRecordingTime(prev => prev + 1), 1000);
    } catch (error) {
      setDialogConfig({ open: true, title: 'Microphone Error', message: 'Could not access microphone. Please check your permissions.', type: 'alert' });
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && isRecording && !isPaused) {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      clearInterval(recordingTimerRef.current);
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current && isRecording && isPaused) {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      recordingTimerRef.current = setInterval(() => setRecordingTime(prev => prev + 1), 1000);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setGlobalRecording(false); // Re-enable swipe navigation
      setIsPaused(false);
      clearInterval(recordingTimerRef.current);
    }
  };

  const discardRecording = () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingTime(0);
  };

  const togglePlayback = () => {
    if (audioRef.current) {
      if (isPlaying) audioRef.current.pause();
      else audioRef.current.play();
      setIsPlaying(!isPlaying);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const transcribeNewAudio = async () => {
    if (!audioBlob) return;
    setIsAudioTranscribing(true);
    try {
      // First upload the audio to save it with the entry
      const audioFormData = new FormData();
      audioFormData.append('audio_data', '');
      
      // Save audio by sending it through the with-audio endpoint as an update
      const reader = new FileReader();
      const base64Audio = await new Promise((resolve) => {
        reader.readAsDataURL(audioBlob);
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
      });
      
      // Transcribe first
      const transcribeForm = new FormData();
      transcribeForm.append('audio', audioBlob, 'recording.webm');
      const response = await axios.post(`${API}/transcribe`, transcribeForm, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success && response.data.text) {
        // Save the audio file to the entry
        await axios.post(`${API}/journal/${entryId}/add-audio`, { audio_data: base64Audio });
        
        // Add transcribed text as new line
        setContent(prev => prev.trim() ? prev + '\n\n' + response.data.text : response.data.text);
        setHasChanges(true);
        discardRecording();
        // Refresh entry to get updated audio info
        await fetchEntry();
      }
    } catch (error) {
      console.error('Transcribe error:', error);
      setDialogConfig({ open: true, title: 'Error', message: 'Failed to transcribe audio. Please try again.', type: 'alert' });
    } finally {
      setIsAudioTranscribing(false);
    }
  };

  const handleSave = async () => {
    if (!hasChanges) return;
    setIsSaving(true);
    try {
      // Upload any new images
      let uploadedFilenames = [];
      let uploadedRecords = [];
      if (newImages.length > 0) {
        const uploaded = await uploadNewImages();
        uploadedFilenames = uploaded.filenames;
        uploadedRecords = uploaded.imageRecords;
      }
      const allImageFilenames = [...existingImages, ...uploadedFilenames];
      
      // Build images array from entry's existing records + new
      const existingRecords = (entry.images || []).filter(img => existingImages.includes(img.id));
      const allImageRecords = [...existingRecords, ...uploadedRecords];
      
      await axios.put(`${API}/journal/${entryId}`, { content, image_filenames: allImageFilenames, images: allImageRecords });
      setHasChanges(false);
      setEntry({ ...entry, content, image_filenames: allImageFilenames });
      setExistingImages(allImageFilenames);
      newImages.forEach(img => URL.revokeObjectURL(img.preview));
      setNewImages([]);
    } catch (error) {
      console.error('Error saving entry:', error);
      setDialogConfig({ open: true, title: 'Save Failed', message: 'Failed to save. Please try again.', type: 'alert' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    if (hasChanges) {
      setDialogConfig({
        open: true,
        title: 'Unsaved Changes',
        message: 'You have unsaved changes. Would you like to save before leaving?',
        type: 'save-discard',
        onConfirm: () => {
          setDialogConfig({ open: false });
          handleSave().then(() => navigate(-1));
        },
        onCancel: () => {
          setDialogConfig({ open: false });
          navigate(-1);
        }
      });
    } else {
      navigate(-1);
    }
  };

  const handleTranscribe = async () => {
    setIsTranscribing(true);
    try {
      const response = await axios.post(`${API}/journal/${entryId}/transcribe`);
      if (response.data.success) {
        setDialogConfig({ open: true, title: 'Done', message: 'Transcription complete!', type: 'alert' });
        await fetchEntry();
      }
    } catch (error) {
      setDialogConfig({ open: true, title: 'Error', message: 'Failed to transcribe. Please try again.', type: 'alert' });
    } finally {
      setIsTranscribing(false);
    }
  };

  const handleDeleteEntry = () => {
    setDialogConfig({
      open: true,
      title: 'Delete Entry',
      message: 'This will permanently delete this journal entry and all stories extracted from it. This cannot be undone.',
      type: 'confirm',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: async () => {
        setDialogConfig({ open: false });
        setIsDeleting(true);
        try {
          await axios.delete(`${API}/journal/${entryId}`);
          navigate('/', { replace: true });
        } catch (error) {
          console.error('Error deleting entry:', error);
          setDialogConfig({ open: true, title: 'Error', message: 'Failed to delete entry. Please try again.', type: 'alert' });
          setIsDeleting(false);
        }
      },
      onCancel: () => setDialogConfig({ open: false })
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Sparkles className="w-12 h-12 text-primary animate-pulse" />
      </div>
    );
  }

  if (!entry) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background px-4">
        <h1 className="text-2xl font-serif text-foreground mb-4">Entry not found</h1>
        <button onClick={() => navigate('/')} className="text-primary hover:text-[#7A9186] transition-colors">Return to Journal</button>
      </div>
    );
  }


  const handleShareFromEdit = async () => {
    if (!shareEmail.trim() || !entryId) return;
    setShareLoading(true);
    setShareError('');
    setShareSuccess('');
    try {
      await axios.post(`${API}/api/entries/${entryId}/share`, { email: shareEmail.trim().toLowerCase() });
      setShareSuccess('Entry shared successfully!');
      setTimeout(() => { setShowShareModal(false); setShareEmail(''); setShareSuccess(''); }, 1500);
    } catch (err) {
      setShareError(err.response?.data?.detail || 'Failed to share');
    } finally {
      setShareLoading(false);
    }
  };


  const entryDate = new Date(entry.timestamp);
  const totalImages = existingImages.length + newImages.length;

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={pageTransition} className="min-h-screen bg-background">
      <div className="fixed top-0 left-0 right-0 bg-background/95 backdrop-blur-sm border-b border-border z-40">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
          <button onClick={handleBack} className="flex items-center space-x-2 text-muted-foreground hover:text-foreground transition-colors" data-testid="edit-back-button">
            <ArrowLeft className="w-5 h-5" /><span>Back</span>
          </button>
          <div className="text-center">
            <h1 className="text-lg md:text-xl font-serif font-medium text-foreground">Edit Journal Entry</h1>
            <p className="text-xs md:text-sm text-muted-foreground">{entryDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => { setShowShareModal(true); setShareEmail(''); setShareError(''); setShareSuccess(''); }}
              className="p-2.5 rounded-full bg-primary text-white hover:bg-primary/90 shadow-lg hover:shadow-xl transition-all"
              data-testid="edit-share-button"
              title="Share Entry"
            >
              <UserPlus className="w-5 h-5 stroke-[2.5]" />
            </button>
          </div>
        </div>
      </div>

      <div className="pt-24 pb-32 px-4 md:px-6">
        <div className="max-w-4xl mx-auto space-y-4">
          {/* Text editor */}
          <div className="bg-card border border-border rounded-2xl p-4 md:p-8 shadow-lg">
            <textarea value={content} onChange={handleContentChange} className="w-full min-h-[30vh] md:min-h-[50vh] bg-transparent border-none outline-none resize-none text-base md:text-lg leading-relaxed font-serif text-foreground placeholder-muted-foreground/50" placeholder="Write your thoughts..." data-testid="edit-entry-textarea" />
          </div>

          {/* Audio Recordings - combined existing + new */}
          <div className="bg-card border border-border rounded-2xl p-4 md:p-6">
            <h3 className="text-sm font-medium text-foreground flex items-center space-x-2 mb-3">
              <Mic className="w-4 h-4 text-primary" />
              <span>Audio Recordings</span>
            </h3>

            {/* Existing audio */}
            {entry.has_audio && !entry.audio_missing && (
              <div className="mb-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-muted-foreground">Original recording</span>
                  {!entry.is_transcribed && (
                    <button onClick={handleTranscribe} disabled={isTranscribing} className="text-xs bg-accent/10 text-accent hover:bg-accent/20 rounded-full px-3 py-1 font-medium transition-colors disabled:opacity-50 flex items-center space-x-1" data-testid="edit-transcribe-button">
                      {isTranscribing ? (<><Sparkles className="w-3 h-3 animate-pulse" /><span>Transcribing...</span></>) : (<><Sparkles className="w-3 h-3" /><span>Transcribe</span></>)}
                    </button>
                  )}
                </div>
                <AudioErrorBoundary><AudioPlayer entryId={entry.id} /></AudioErrorBoundary>
              </div>
            )}
            {entry.has_audio && entry.audio_missing && (
              <div className="mb-3 p-2 bg-secondary/30 rounded-lg">
                <p className="text-xs text-muted-foreground">Original audio file no longer available</p>
              </div>
            )}

            {/* New recording preview */}
            {audioUrl && (
              <audio ref={audioRef} src={audioUrl} onEnded={() => setIsPlaying(false)} className="hidden" />
            )}

            {audioBlob && !isRecording && (
              <div className="mb-3 p-3 bg-primary/5 border border-primary/15 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-muted-foreground">New recording</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex items-center space-x-3 bg-secondary/50 rounded-full px-4 py-2">
                    <button onClick={togglePlayback} className="text-primary hover:text-[#7A9186] transition-colors" data-testid="edit-playback-button">
                      {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                    </button>
                    <Volume2 className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">{formatTime(recordingTime)}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button onClick={transcribeNewAudio} disabled={isAudioTranscribing} className="flex items-center space-x-1 bg-accent/10 text-accent hover:bg-accent/20 rounded-full px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50" data-testid="edit-transcribe-new-audio">
                      {isAudioTranscribing ? (<><Sparkles className="w-4 h-4 animate-pulse" /><span>Transcribing...</span></>) : (<><Sparkles className="w-4 h-4" /><span>Transcribe & Add</span></>)}
                    </button>
                    <button onClick={discardRecording} className="flex items-center space-x-1 text-muted-foreground hover:text-red-500 transition-colors text-sm" data-testid="edit-discard-recording">
                      <X className="w-4 h-4" /><span>Discard</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {isRecording && (
              <div className="flex items-center space-x-3 p-3 bg-red-500/5 border border-red-500/15 rounded-xl">
                <div className="flex items-center space-x-2">
                  {isPaused ? <span className="w-3 h-3 bg-orange-500 rounded-full" /> : <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />}
                  <span className={`text-sm font-medium ${isPaused ? 'text-orange-500' : 'text-red-500'}`}>
                    {isPaused ? 'Paused' : 'Recording'} {formatTime(recordingTime)}
                  </span>
                </div>
                {isPaused ? (
                  <button onClick={resumeRecording} className="flex items-center space-x-2 bg-primary text-white rounded-full px-4 py-2 text-sm hover:bg-primary/80 transition-colors" data-testid="edit-resume-recording">
                    <Play className="w-4 h-4" /><span>Resume</span>
                  </button>
                ) : (
                  <button onClick={pauseRecording} className="flex items-center space-x-2 bg-orange-500 text-white rounded-full px-4 py-2 text-sm hover:bg-orange-600 transition-colors" data-testid="edit-pause-recording">
                    <Pause className="w-4 h-4" /><span>Pause</span>
                  </button>
                )}
                <button onClick={stopRecording} className="flex items-center space-x-2 bg-red-500 text-white rounded-full px-4 py-2 text-sm hover:bg-red-600 transition-colors" data-testid="edit-stop-recording">
                  <Square className="w-4 h-4" /><span>Stop</span>
                </button>
              </div>
            )}

            {/* Add recording button */}
            {!audioBlob && !isRecording && (
              <button
                onClick={startRecording}
                className="flex items-center space-x-2 text-muted-foreground hover:text-primary transition-colors bg-secondary/30 rounded-xl px-4 py-3 w-full justify-center mt-2"
                data-testid="edit-start-recording"
              >
                <Mic className="w-5 h-5" />
                <span className="text-sm">Add audio recording</span>
              </button>
            )}
          </div>

          {/* Attached Images */}
          <div className="bg-card border border-border rounded-2xl p-4 md:p-6">
            <h3 className="text-sm font-medium text-foreground flex items-center space-x-2 mb-3">
              <Paperclip className="w-4 h-4 text-primary" />
              <span>Images {totalImages > 0 ? `(${totalImages})` : ''}</span>
            </h3>
            <div className="flex items-center gap-3 flex-wrap">
              {existingImages.map((imageId, idx) => (
                <div key={`existing-${idx}`} className="relative group w-20 h-20 md:w-24 md:h-24 rounded-xl overflow-hidden border border-border shadow-sm cursor-pointer bg-secondary/30" onClick={() => setLightboxImage({ imageUrl: getImgUrl(entry, imageId, 'full'), downloadUrl: getDownloadUrl(entry, imageId) })}>
                  <img src={getImgUrl(entry, imageId, 'thumb')} alt="" loading="lazy" className="w-full h-full object-cover" data-testid={`edit-existing-image-${idx}`} />
                  <button onClick={(e) => { e.stopPropagation(); removeExistingImage(idx); }} className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-existing-image-${idx}`}><X className="w-3 h-3" /></button>
                  <a href={getDownloadUrl(entry, imageId)} onClick={(e) => e.stopPropagation()} className="absolute bottom-0.5 right-0.5 bg-black/60 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" title="Download Original"><Download className="w-3 h-3" /></a>
                </div>
              ))}
              {newImages.map((img, idx) => (
                <div key={`new-${idx}`} className="relative group w-20 h-20 md:w-24 md:h-24 rounded-xl overflow-hidden border-2 border-dashed border-primary/30 shadow-sm cursor-pointer" onClick={() => setLightboxImage({ imageUrl: img.preview, downloadUrl: null })}>
                  <img src={img.preview} alt="" className="w-full h-full object-cover" data-testid={`edit-new-image-${idx}`} />
                  <button onClick={(e) => { e.stopPropagation(); removeNewImage(idx); }} className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-new-image-${idx}`}><X className="w-3 h-3" /></button>
                  <div className="absolute bottom-0 left-0 right-0 bg-primary/80 text-white text-[9px] text-center py-0.5">New</div>
                </div>
              ))}
              {totalImages < 10 && (
                <button onClick={() => imageInputRef.current?.click()} className="w-20 h-20 md:w-24 md:h-24 rounded-xl border-2 border-dashed border-border hover:border-primary/50 flex flex-col items-center justify-center text-muted-foreground hover:text-primary transition-colors" data-testid="edit-upload-more-images">
                  <Paperclip className="w-5 h-5 mb-1" /><span className="text-[10px]">Add</span>
                </button>
              )}
            </div>
            <input ref={imageInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp,.jpg,.jpeg,.png,.gif,.webp" multiple onChange={handleImageSelect} className="hidden" data-testid="edit-image-file-input" />
          </div>

          {/* Delete Entry */}
          <div className="pt-4 pb-2">
            <button
              onClick={handleDeleteEntry}
              disabled={isDeleting}
              className="flex items-center space-x-2 text-sm text-muted-foreground hover:text-red-500 transition-colors mx-auto disabled:opacity-50"
              data-testid="edit-delete-entry-button"
            >
              <Trash2 className="w-4 h-4" />
              <span>{isDeleting ? 'Deleting...' : 'Delete this entry'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Save button */}
      <div className="fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur-sm border-t border-border z-40">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-4">
          <button onClick={handleSave} disabled={!hasChanges || isSaving} className={`w-full flex items-center justify-center space-x-2 py-4 rounded-full font-medium transition-all duration-300 ${hasChanges ? 'bg-primary text-white hover:bg-primary/80 shadow-md hover:shadow-lg opacity-100' : 'bg-primary text-white cursor-not-allowed opacity-70'}`} data-testid="edit-save-button">
            {isSaving ? (<><Sparkles className="w-5 h-5 animate-spin" /><span>Saving...</span></>) : (<><Save className="w-5 h-5" /><span>Save Changes</span></>)}
          </button>
        </div>
      </div>

      {/* Image Lightbox */}
      {lightboxImage && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-[200] p-4"
          onClick={() => setLightboxImage(null)}
          data-testid="edit-image-lightbox"
        >
          <button onClick={() => setLightboxImage(null)} className="absolute top-4 right-4 text-white/80 hover:text-white bg-black/40 rounded-full w-10 h-10 flex items-center justify-center">
            <X className="w-6 h-6" />
          </button>
          {lightboxImage.downloadUrl && (
            <a href={lightboxImage.downloadUrl} onClick={(e) => e.stopPropagation()} className="absolute top-4 left-4 text-white/80 hover:text-white bg-black/40 rounded-full w-10 h-10 flex items-center justify-center" title="Download">
              <Download className="w-5 h-5" />
            </a>
          )}
          <img src={lightboxImage.imageUrl} alt="" className="max-w-full max-h-[85vh] object-contain rounded-xl shadow-2xl" onClick={(e) => e.stopPropagation()} />
        </motion.div>
      )}

      {/* Themed Dialog */}
      <ThemedDialog
        open={dialogConfig.open}
        title={dialogConfig.title}
        message={dialogConfig.message}
        type={dialogConfig.type}
        onConfirm={dialogConfig.onConfirm || (() => setDialogConfig({ open: false }))}
        onCancel={dialogConfig.onCancel || (() => setDialogConfig({ open: false }))}
        confirmText={dialogConfig.confirmText || (dialogConfig.type === 'save-discard' ? 'Save' : 'OK')}
        cancelText={dialogConfig.cancelText || (dialogConfig.type === 'save-discard' ? 'Discard' : 'Cancel')}
      />

      {/* Share Entry Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setShowShareModal(false)} data-testid="edit-share-modal">
          <div className="bg-card-solid rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Users className="w-5 h-5 text-accent" />
                <h3 className="text-lg font-serif font-medium">Share Journal Entry</h3>
              </div>
              <button onClick={() => setShowShareModal(false)} className="p-1 rounded-full hover:bg-secondary transition-colors"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Share this entry with another NOYO user. It will appear in their "Shared with Me" folder.</p>
            <input
              type="email"
              value={shareEmail}
              onChange={(e) => setShareEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && shareEmail.trim()) {
                  e.preventDefault();
                  handleShareFromEdit();
                }
              }}
              placeholder="Enter their email address"
              className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 mb-3"
              autoFocus
              data-testid="edit-share-email-input"
            />
            {shareError && <p className="text-sm text-red-500 mb-2">{shareError}</p>}
            {shareSuccess && <p className="text-sm text-green-600 mb-2">{shareSuccess}</p>}
            <button
              onClick={handleShareFromEdit}
              disabled={shareLoading || !shareEmail.trim()}
              className="w-full py-3 bg-primary text-white rounded-xl font-medium hover:bg-[#7A9186] transition-colors disabled:opacity-50"
              data-testid="edit-share-submit"
            >
              {shareLoading ? 'Sharing...' : 'Share Entry'}
            </button>
          </div>
        </div>
      )}

    </motion.div>
  );
}

export default EditEntryPage;
